import { useMemo } from "react";
import {
  useGetMessageStatsQuery,
  type GetMessageStatsQuery,
} from "src/graphql";
import type { EngagementChartDatum } from "src/cep/messages/components/engagement-over-time-card";
import type { ChannelType } from "src/types/cep";
import { roundToDecimalPlace } from "src/utils/numbers";

type StatsRefetchInterval =
  | number
  | false
  | ((data: GetMessageStatsQuery | undefined) => number | false);

export type AggregatedMessageStats = {
  totalSent: number;
  totalDelivered: number;
  totalOpens: number;
  totalClicks: number;
  totalDevicesAccepted: number | null;
  totalDevicesRejected: number | null;
  totalDeviceOpens: number | null;
  deliveryRate: number;
  openRate: number;
  clickRate: number;
};

export type PerMessageStats = {
  totalSent: number;
  openRate: number;
  clickRate: number;
};

export function useAggregatedMessageStats(
  messageIds: string[],
  since: string,
  {
    campaignRunId,
    channelType,
    refetchInterval,
    enabled = true,
  }: {
    campaignRunId?: string;
    channelType?: ChannelType;
    refetchInterval?: StatsRefetchInterval;
    enabled?: boolean;
  } = {},
) {
  const { data: statsData, isLoading } = useGetMessageStatsQuery(
    { messageIds, since, campaignRunId },
    { enabled: enabled && messageIds.length > 0, refetchInterval },
  );

  const { aggregatedStats, chartData, perMessageStats } = useMemo(() => {
    const allStats = statsData?.getMessageStats ?? [];

    let totalSent = 0;
    let totalDelivered = 0;
    let totalOpens = 0;
    let totalClicks = 0;
    // Stay null until a row contributes a number
    // the backend writes NULL for non-push channels
    let totalDevicesAccepted: number | null = null;
    let totalDevicesRejected: number | null = null;
    let totalDeviceOpens: number | null = null;
    const dailyMap = new Map<string, EngagementChartDatum>();
    const perMessage = new Map<string, PerMessageStats>();

    for (const stat of allStats) {
      totalSent += stat.totalSent;
      totalDelivered += stat.totalDelivered;
      totalOpens += stat.totalOpens;
      totalClicks += stat.totalClicks;
      if (stat.totalDevicesAccepted !== null) {
        totalDevicesAccepted =
          (totalDevicesAccepted ?? 0) + stat.totalDevicesAccepted;
      }
      if (stat.totalDevicesRejected !== null) {
        totalDevicesRejected =
          (totalDevicesRejected ?? 0) + stat.totalDevicesRejected;
      }
      if (stat.totalDeviceOpens !== null) {
        totalDeviceOpens = (totalDeviceOpens ?? 0) + stat.totalDeviceOpens;
      }

      // Reporting rates use sends consistently across every channel and page.
      const msgRateDenom = stat.totalSent;
      const msgOpenRate =
        msgRateDenom > 0
          ? roundToDecimalPlace((stat.totalOpens / msgRateDenom) * 100, 1)
          : 0;
      const msgClickRate =
        msgRateDenom > 0
          ? roundToDecimalPlace((stat.totalClicks / msgRateDenom) * 100, 1)
          : 0;
      perMessage.set(stat.messageId, {
        totalSent: stat.totalSent,
        openRate: msgOpenRate,
        clickRate: msgClickRate,
      });

      for (const day of stat.daily) {
        const existing = dailyMap.get(day.day);
        if (existing) {
          existing.sent += day.totalSent;
          existing.opened += day.totalOpens;
          existing.clicked += day.totalClicks;
        } else {
          dailyMap.set(day.day, {
            date: day.day,
            sent: day.totalSent,
            opened: day.totalOpens,
            clicked: day.totalClicks,
          });
        }
      }
    }

    const rateDenom = totalSent;
    // Clamp the delivery rate at 100%. The dispatcher can under-count
    // `total_sent_dispatcher` if it's killed after handing a message to the
    // provider but before recording the send, while the provider's delivery
    // webhook still arrives. This is an edge that should rarely happen but
    // we protect the display here
    const deliveryRate =
      totalSent > 0
        ? Math.min(
            100,
            roundToDecimalPlace((totalDelivered / totalSent) * 100, 1),
          )
        : 0;
    const openRate =
      rateDenom > 0
        ? roundToDecimalPlace((totalOpens / rateDenom) * 100, 1)
        : 0;
    const clickRate =
      rateDenom > 0
        ? roundToDecimalPlace((totalClicks / rateDenom) * 100, 1)
        : 0;

    return {
      aggregatedStats: {
        totalSent,
        totalDelivered,
        totalOpens,
        totalClicks,
        totalDevicesAccepted,
        totalDevicesRejected,
        totalDeviceOpens,
        deliveryRate,
        openRate,
        clickRate,
      },
      chartData: Array.from(dailyMap.values()).sort((a, b) =>
        a.date.localeCompare(b.date),
      ),
      perMessageStats: perMessage,
    };
  }, [statsData]);

  return { aggregatedStats, chartData, perMessageStats, isLoading };
}
